<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';

const permissions = defineModel({
  type: Object,
  default: () => ({
    agentIds: [],
    isPublic: false,
  }),
});

const { t } = useI18n();
const store = useStore();

const agents = useMapGetter('agents/getAgents');
const currentUser = useMapGetter('getCurrentUser');

const selectedAgents = ref([]);
const availableAgents = ref([]);

const visibilityOptions = [
  {
    id: 'private',
    label: t('KANBAN.PIPELINE_MODAL.PERMISSIONS.PRIVATE'),
    description: t('KANBAN.PIPELINE_MODAL.PERMISSIONS.PRIVATE_DESC'),
    icon: 'i-lucide-lock',
  },
  {
    id: 'agents',
    label: t('KANBAN.PIPELINE_MODAL.PERMISSIONS.SPECIFIC_AGENTS'),
    description: t('KANBAN.PIPELINE_MODAL.PERMISSIONS.SPECIFIC_AGENTS_DESC'),
    icon: 'i-lucide-users',
  },
  {
    id: 'public',
    label: t('KANBAN.PIPELINE_MODAL.PERMISSIONS.ALL_AGENTS'),
    description: t('KANBAN.PIPELINE_MODAL.PERMISSIONS.ALL_AGENTS_DESC'),
    icon: 'i-lucide-globe',
  },
];

const selectedVisibility = ref('private');

const isCurrentUserAdmin = computed(() => {
  return currentUser.value?.role === 'administrator';
});

const agentOptions = computed(() => {
  return agents.value.map(agent => ({
    id: agent.id,
    label: agent.name,
    value: agent.id,
    subtitle: agent.email,
    avatar: agent.avatar_url,
  }));
});

const selectedAgentOptions = computed(() => {
  return selectedAgents.value.map(agentId => {
    const agent = agents.value.find(a => a.id === agentId);
    return {
      id: agent.id,
      label: agent.name,
      value: agent.id,
      subtitle: agent.email,
      avatar: agent.avatar_url,
    };
  });
});

const updatePermissions = () => {
  switch (selectedVisibility.value) {
    case 'private':
      permissions.value = {
        agentIds: [currentUser.value.id],
        isPublic: false,
      };
      break;
    case 'agents':
      permissions.value = {
        agentIds: [...selectedAgents.value],
        isPublic: false,
      };
      break;
    case 'public':
      permissions.value = {
        agentIds: [],
        isPublic: true,
      };
      break;
  }
};

const handleVisibilityChange = (value) => {
  selectedVisibility.value = value;
  updatePermissions();
};

const handleAgentSelection = (agentIds) => {
  selectedAgents.value = agentIds;
  updatePermissions();
};

const addAgent = (agent) => {
  if (!selectedAgents.value.includes(agent.id)) {
    selectedAgents.value.push(agent.id);
    updatePermissions();
  }
};

const removeAgent = (agentId) => {
  selectedAgents.value = selectedAgents.value.filter(id => id !== agentId);
  updatePermissions();
};

onMounted(() => {
  // Load agents if not already loaded
  if (agents.value.length === 0) {
    store.dispatch('agents/get');
  }

  // Initialize based on existing permissions
  if (permissions.value.isPublic) {
    selectedVisibility.value = 'public';
  } else if (permissions.value.agentIds.length > 1 || 
             (permissions.value.agentIds.length === 1 && permissions.value.agentIds[0] !== currentUser.value.id)) {
    selectedVisibility.value = 'agents';
    selectedAgents.value = [...permissions.value.agentIds];
  } else {
    selectedVisibility.value = 'private';
  }
});
</script>

<template>
  <div class="space-y-6">
    <div>
      <h3 class="text-lg font-medium text-n-slate-12 mb-2">
        {{ t('KANBAN.PIPELINE_MODAL.PERMISSIONS.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11 mb-4">
        {{ t('KANBAN.PIPELINE_MODAL.PERMISSIONS.SUBTITLE') }}
      </p>
    </div>

    <!-- Visibility Options -->
    <div class="space-y-3">
      <div
        v-for="option in visibilityOptions"
        :key="option.id"
        @click="handleVisibilityChange(option.id)"
        class="border border-n-weak rounded-lg p-4 cursor-pointer transition-all hover:border-blue-300"
        :class="{
          'border-blue-500 bg-blue-50': selectedVisibility === option.id,
          'border-n-weak': selectedVisibility !== option.id,
        }"
      >
        <div class="flex items-start gap-3">
          <div class="mt-1">
            <input
              type="radio"
              :checked="selectedVisibility === option.id"
              @change="handleVisibilityChange(option.id)"
              class="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
            />
          </div>
          <div class="flex-1">
            <div class="flex items-center gap-2 mb-1">
              <i :class="option.icon" class="size-4 text-n-slate-9" />
              <h4 class="font-medium text-n-slate-12">{{ option.label }}</h4>
            </div>
            <p class="text-sm text-n-slate-11">{{ option.description }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Agent Selection (only show when "agents" is selected) -->
    <div v-if="selectedVisibility === 'agents'" class="space-y-4">
      <div>
        <label class="block text-sm font-medium text-n-slate-12 mb-2">
          {{ t('KANBAN.PIPELINE_MODAL.PERMISSIONS.SELECT_AGENTS') }}
        </label>
        <p class="text-xs text-n-slate-11 mb-3">
          {{ t('KANBAN.PIPELINE_MODAL.PERMISSIONS.SELECT_AGENTS_DESC') }}
        </p>
      </div>

      <!-- Agent selector -->
      <div class="border border-n-weak rounded-lg p-4">
        <SelectMenu
          :options="agentOptions"
          :selected="selectedAgents"
          multiple
          searchable
          :placeholder="t('KANBAN.PIPELINE_MODAL.PERMISSIONS.SEARCH_AGENTS')"
          @update:selected="handleAgentSelection"
        />
      </div>

      <!-- Selected agents list -->
      <div v-if="selectedAgentOptions.length > 0" class="space-y-2">
        <h5 class="text-sm font-medium text-n-slate-12">
          {{ t('KANBAN.PIPELINE_MODAL.PERMISSIONS.SELECTED_AGENTS') }} ({{ selectedAgentOptions.length }})
        </h5>
        <div class="space-y-2">
          <div
            v-for="agent in selectedAgentOptions"
            :key="agent.id"
            class="flex items-center justify-between p-2 bg-n-solid-2 rounded-lg"
          >
            <div class="flex items-center gap-2">
              <img
                v-if="agent.avatar"
                :src="agent.avatar"
                :alt="agent.label"
                class="w-6 h-6 rounded-full"
              />
              <div
                v-else
                class="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-medium"
              >
                {{ agent.label.charAt(0).toUpperCase() }}
              </div>
              <div>
                <p class="text-sm font-medium text-n-slate-12">{{ agent.label }}</p>
                <p class="text-xs text-n-slate-11">{{ agent.subtitle }}</p>
              </div>
            </div>
            <NextButton
              variant="ghost"
              size="xs"
              @click="removeAgent(agent.id)"
              class="text-red-500 hover:text-red-700"
            >
              <i class="i-lucide-x size-4" />
            </NextButton>
          </div>
        </div>
      </div>
    </div>

    <!-- Current user info (always show) -->
    <div class="border border-n-weak rounded-lg p-4 bg-n-solid-1">
      <div class="flex items-center gap-2 mb-2">
        <i class="i-lucide-info size-4 text-blue-500" />
        <h5 class="text-sm font-medium text-n-slate-12">
          {{ t('KANBAN.PIPELINE_MODAL.PERMISSIONS.CREATOR_INFO') }}
        </h5>
      </div>
      <p class="text-sm text-n-slate-11">
        {{ t('KANBAN.PIPELINE_MODAL.PERMISSIONS.CREATOR_INFO_DESC', { name: currentUser?.name }) }}
      </p>
    </div>
  </div>
</template>
